/**
 * variant-picker-v2.js
 *
 * Wires the v2 product variant picker — each option group contains both a
 * Tailwind-styled dropdown (top) and the original radio button / color swatch
 * row (bottom). The radios are the source of truth: <variant-selects> (from
 * variant-selects-v2.js) listens on their `change` event to update price /
 * variant image / URL.
 *
 * IMPORTANT: `assets/product-info.js` replaces the entire <variant-selects>
 * subtree on every variant change via HTMLUpdateUtility.viewTransition. That
 * means any per-element event listeners bound on mount are destroyed after
 * the first variant switch. To survive DOM replacement, every listener in
 * this file is attached to `document` and uses event delegation via
 * `closest()`. The Liquid template is the source of truth for initial state
 * (selected-swatch style, data-state="closed", checked radio, check-icon
 * visibility) — this script only handles user interaction and dropdown ↔
 * radio synchronization.
 *
 * All Tailwind classes toggled below are also present in
 * snippets/product-variant-picker-v2.liquid so the JIT scanner keeps them.
 */
(function () {
  if (window.__variantPickerV2Init) return;
  window.__variantPickerV2Init = true;

  // Set by the menu drag-scroll when a grab-drag past threshold ends, so the
  // ensuing click doesn't select an option (see handleClick option branch).
  let suppressNextClickUntil = 0;

  document.addEventListener('click', handleClick, true);
  document.addEventListener('keydown', handleKeydown);
  document.addEventListener('change', handleChange);

  /**
   * @param {MouseEvent} event
   */
  function handleClick(event) {
    // 0. Category filter chip click
    const chip = event.target.closest && event.target.closest('[data-v2-category-filter]');
    if (chip) {
      event.preventDefault();
      const group = chip.closest('[data-variant-option-group]');
      if (!group) return;
      const category = chip.dataset.category || '__all__';
      categoryFilterState.set(getFilterKey(group), category);
      applyCategoryFilter(group, category);
      return;
    }

    // 1. Dropdown toggle button
    const toggle = event.target.closest && event.target.closest('[data-variant-dropdown-toggle]');
    if (toggle) {
      const dropdown = toggle.closest('[data-variant-dropdown-v2]');
      if (!dropdown) return;
      // Single-option-value picker: dropdown is rendered for visual parity but
      // is not interactive (Liquid sets data-disabled="true" + button.disabled
      // on the toggle). Bail before any open/close state change.
      if (dropdown.getAttribute('data-disabled') === 'true' || toggle.disabled) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      const wasExpanded = toggle.getAttribute('aria-expanded') === 'true';
      closeAllDropdowns(dropdown);
      if (!wasExpanded) {
        openDropdown(dropdown);
      } else {
        closeDropdown(dropdown);
      }
      return;
    }

    // 2. Dropdown option click
    const opt = event.target.closest && event.target.closest('[data-variant-dropdown-option]');
    if (opt) {
      event.preventDefault();
      // Ignore the click that ends a drag-to-scroll gesture.
      if (Date.now() < suppressNextClickUntil) {
        event.stopPropagation();
        return;
      }
      if (opt.getAttribute('aria-disabled') === 'true') return;
      const dropdown = opt.closest('[data-variant-dropdown-v2]');
      const group = opt.closest('[data-variant-option-group]');
      if (!dropdown || !group) return;

      const targetValue = opt.dataset.value;
      const radios = group.querySelectorAll('input[type="radio"]');
      const matching = Array.from(radios).find((radio) => radio.value === targetValue);

      if (matching && !matching.disabled && !matching.checked) {
        matching.checked = true;
        matching.dispatchEvent(new Event('change', { bubbles: true }));
      }
      closeDropdown(dropdown);
      return;
    }

    // 3. Click outside any dropdown → close all
    if (!event.target.closest || !event.target.closest('[data-variant-dropdown-v2]')) {
      closeAllDropdowns();
    }
  }

  /**
   * @param {KeyboardEvent} event
   */
  function handleKeydown(event) {
    if (event.key === 'Escape') closeAllDropdowns();
  }

  /**
   * Delegated `change` handler — when a radio input inside an option group
   * changes (from a direct swatch click OR from a dropdown option click that
   * programmatically toggles the matching radio), mirror the new value into
   * the dropdown toggle's label + swatch preview and the menu's check icon /
   * aria-selected state.
   *
   * @param {Event} event
   */
  function handleChange(event) {
    const target = event.target;
    if (!target || target.tagName !== 'INPUT' || target.type !== 'radio' || !target.checked) return;
    const group = target.closest('[data-variant-option-group]');
    if (!group) return;
    const dropdown = group.querySelector('[data-variant-dropdown-v2]');
    if (!dropdown) return;
    syncDropdownFromValue(dropdown, target.value);
  }

  /**
   * "More below" scroll cue (edge-fade pattern — see
   * docs/theme_function/thumbnail-edge-fade-gradient.md): show the sticky fade + chevron
   * (last li, `data-visible`) only while the menu's content overflows its
   * max-height AND it isn't scrolled to the end.
   * @param {HTMLElement} menu
   */
  function updateDropdownMore(menu) {
    if (!menu) return;
    const more = menu.querySelector('[data-variant-dropdown-more]');
    if (!more) return;
    const overflow = menu.scrollHeight - menu.clientHeight;
    const atBottom = menu.scrollTop >= overflow - 2;
    more.setAttribute('data-visible', overflow > 2 && !atBottom ? 'true' : 'false');
  }

  /**
   * @param {HTMLElement} dropdown
   */
  function openDropdown(dropdown) {
    dropdown.setAttribute('data-state', 'open');
    const toggle = dropdown.querySelector('[data-variant-dropdown-toggle]');
    if (toggle) toggle.setAttribute('aria-expanded', 'true');
    const menu = dropdown.querySelector('[data-variant-dropdown-menu]');
    if (menu) {
      if (!menu.__moreWired) {
        menu.__moreWired = true;
        menu.addEventListener('scroll', () => updateDropdownMore(menu), { passive: true });
      }
      requestAnimationFrame(() => updateDropdownMore(menu));
    }
  }

  /**
   * @param {HTMLElement} dropdown
   */
  function closeDropdown(dropdown) {
    dropdown.setAttribute('data-state', 'closed');
    const toggle = dropdown.querySelector('[data-variant-dropdown-toggle]');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
  }

  /**
   * @param {HTMLElement} [except] — optional dropdown to leave untouched
   */
  function closeAllDropdowns(except) {
    document.querySelectorAll('[data-variant-dropdown-v2][data-state="open"]').forEach((dropdown) => {
      if (dropdown === except) return;
      closeDropdown(dropdown);
    });
  }

  /**
   * @param {HTMLElement} dropdown
   * @param {string} value
   */
  function syncDropdownFromValue(dropdown, value) {
    const match = dropdown.querySelector(
      '[data-variant-dropdown-option][data-value="' + cssEscape(value) + '"]'
    );
    if (!match) return;

    const label = dropdown.querySelector('[data-variant-dropdown-selected-label]');
    const selectedSwatch = dropdown.querySelector('[data-variant-dropdown-selected-swatch]');
    if (label) label.textContent = value;

    if (selectedSwatch) {
      const hex = match.dataset.swatchHex || '';
      const img = match.dataset.swatchImg || '';
      const base =
        'width: var(--v2-swatch-size); height: var(--v2-swatch-size);' +
        ' box-shadow: inset 0 0 0 1px rgba(var(--color-foreground),0.14), inset 0 0 0 2px rgba(var(--color-background),0.55);';
      let nextStyle;
      if (hex) {
        nextStyle = base + ' background-color: ' + hex + ';';
      } else if (img) {
        nextStyle =
          base +
          ' background-image: url(' +
          img +
          '); background-size: cover; background-position: center;';
      } else {
        nextStyle =
          base +
          ' background: repeating-linear-gradient(45deg, rgba(var(--color-foreground),0.06) 0 4px, rgba(var(--color-foreground),0.12) 4px 8px);';
      }
      selectedSwatch.setAttribute('style', nextStyle);
    }

    dropdown.querySelectorAll('[data-variant-dropdown-option]').forEach((opt) => {
      const isMatch = opt === match;
      const check = opt.querySelector('[data-variant-dropdown-check]');
      if (check) check.classList.toggle('tw-hidden', !isMatch);
      opt.setAttribute('aria-pressed', isMatch ? 'true' : 'false');
      opt.style.backgroundColor = isMatch ? 'rgba(var(--color-foreground),0.06)' : 'transparent';
    });
  }

  /**
   * @param {string} value
   * @returns {string}
   */
  function cssEscape(value) {
    if (typeof window.CSS !== 'undefined' && typeof window.CSS.escape === 'function') {
      return window.CSS.escape(value);
    }
    return String(value).replace(/"/g, '\\"');
  }

  /**
   * In-memory store for the active category filter, keyed by
   * `${productUrl}::${optionPosition}`. Persists across <variant-selects>
   * subtree replacement because this IIFE lives on the module, not on any
   * DOM node. Liquid always renders "All" as the default pressed chip, so
   * reapplyCategoryFilters() is a no-op unless a non-"All" category has
   * been chosen earlier in the session.
   *
   * @type {Map<string, string>}
   */
  const categoryFilterState = new Map();

  /**
   * @param {HTMLElement} group — the <fieldset data-variant-option-group>
   * @returns {string}
   */
  function getFilterKey(group) {
    const selects = group.closest && group.closest('variant-selects');
    const url = selects && selects.dataset ? selects.dataset.url || '' : '';
    const pos = (group.dataset && group.dataset.optionPosition) || '0';
    return url + '::' + pos;
  }

  /**
   * Filters the swatch-row items inside one color option group by category
   * and slides the single active-indicator under the pressed chip.
   *
   * Visibility is strictly by category: swatches outside the active category
   * are hidden, regardless of whether they represent the currently-selected
   * variant. A selected-but-hidden swatch leaves the underlying variant
   * state (price, image, URL) unchanged — the user must click "All" or the
   * matching category to bring it back into view.
   *
   * The chip bar never dispatches a variant change — it only narrows the
   * display. Actual variant switching happens when the user clicks a
   * visible swatch, which flows through the standard change pipeline.
   *
   * Auto-switching the variant on chip click was removed because it pushed
   * the filter into product-info.js's HTMLUpdateUtility.viewTransition,
   * whose cross-fade captured the pre-MutationObserver chip state and
   * produced a brief flicker of the "All" chip on every category click.
   *
   * @param {HTMLElement} group
   * @param {string} category — trimmed/downcased category or '__all__'
   * @param {{ animated?: boolean }} [options]
   */
  function applyCategoryFilter(group, category, options) {
    if (group.getAttribute('data-is-color') !== 'true') return;
    const animated = !options || options.animated !== false;

    const items = group.querySelectorAll('.v2-picker__swatch-item[data-category]');
    const isAll = category === '__all__';

    items.forEach((item) => {
      const itemCat = (item.dataset.category || '').toLowerCase();
      const show = isAll || itemCat === category;
      item.style.display = show ? '' : 'none';
    });

    let activeChip = null;
    group.querySelectorAll('[data-v2-category-filter]').forEach((chip) => {
      const pressed = (chip.dataset.category || '') === category;
      chip.setAttribute('aria-pressed', pressed ? 'true' : 'false');
      if (pressed) activeChip = chip;
    });

    positionIndicator(group, activeChip, animated);
  }

  /**
   * Moves the single sliding indicator inside the filter-bar's inner
   * wrapper to match the active chip's offsetLeft / offsetWidth. When
   * `animated === false`, the left/width transitions are disabled for
   * one frame so the indicator snaps into place without appearing to
   * slide in from 0 on first paint (initial load + DOM-replace paths).
   *
   * @param {HTMLElement} group
   * @param {HTMLElement|null} activeChip
   * @param {boolean} animated
   */
  function positionIndicator(group, activeChip, animated) {
    const indicator = group.querySelector('[data-v2-active-indicator]');
    if (!indicator || !activeChip) return;

    const left = activeChip.offsetLeft;
    const width = activeChip.offsetWidth;

    if (!animated) {
      const prevTransition = indicator.style.transition;
      indicator.style.transition = 'none';
      indicator.style.left = left + 'px';
      indicator.style.width = width + 'px';
      indicator.style.opacity = '1';
      // Force a reflow so the transition-less change commits before we
      // restore the transition string — otherwise the browser may batch
      // the re-enable with the next style write and animate anyway.
      void indicator.offsetWidth;
      indicator.style.transition = prevTransition;
      return;
    }

    indicator.style.left = left + 'px';
    indicator.style.width = width + 'px';
    indicator.style.opacity = '1';
  }

  /**
   * Initial / non-animated pass over every color option group on the page.
   * Positions the sliding indicator under whichever chip matches the
   * stored filter state (default '__all__'). Runs on DOMContentLoaded,
   * on `document.fonts.ready` (font swap can change chip widths), on
   * window resize (chip widths can shift at breakpoint boundaries), and
   * after each <variant-selects> DOM replacement observed by
   * variantSelectsObserver below.
   *
   * @param {HTMLElement|Document|null} root
   */
  function initIndicators(root) {
    const scope = root || document;
    if (!scope.querySelectorAll) return;
    const groups = scope.querySelectorAll(
      '[data-variant-option-group][data-is-color="true"]'
    );
    groups.forEach((group) => {
      const key = getFilterKey(group);
      const category = categoryFilterState.get(key) || '__all__';
      applyCategoryFilter(group, category, { animated: false });
    });
    enhanceDropdowns(scope);
  }

  /**
   * Per-dropdown: size the box to the LONGEST option (measured off-flow sizer),
   * and wire drag-to-scroll on the option menu. Idempotent — safe on every
   * DOMContentLoaded / fonts.ready / resize / <variant-selects> re-insert.
   * @param {HTMLElement|Document|null} root
   */
  function enhanceDropdowns(root) {
    const scope = root || document;
    if (!scope.querySelectorAll) return;
    scope.querySelectorAll('[data-variant-dropdown-v2]').forEach((dropdown) => {
      const sizer = dropdown.querySelector('[data-variant-dropdown-sizer]');
      if (sizer) {
        const w = Math.ceil(sizer.getBoundingClientRect().width);
        if (w > 0) dropdown.style.minWidth = w + 'px';
      }
      const menu = dropdown.querySelector('[data-variant-dropdown-menu]');
      if (menu && !menu.dataset.dragScroll) {
        menu.dataset.dragScroll = '1';
        setupMenuDragScroll(menu);
      }
    });
  }

  function setupMenuDragScroll(menu) {
    let down = false;
    let startY = 0;
    let startScroll = 0;
    let moved = 0;
    menu.addEventListener('pointerdown', (e) => {
      if (e.button != null && e.button !== 0) return;
      down = true;
      moved = 0;
      startY = e.clientY;
      startScroll = menu.scrollTop;
      menu.classList.add('is-dragging');
    });
    menu.addEventListener('pointermove', (e) => {
      if (!down) return;
      const dy = e.clientY - startY;
      if (Math.abs(dy) > moved) moved = Math.abs(dy);
      menu.scrollTop = startScroll - dy;
    });
    const end = () => {
      if (!down) return;
      down = false;
      if (moved > 6) suppressNextClickUntil = Date.now() + 80;
      menu.classList.remove('is-dragging');
    };
    menu.addEventListener('pointerup', end);
    menu.addEventListener('pointerleave', end);
    menu.addEventListener('pointercancel', end);
  }

  // Initial placement on module load.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initIndicators(document);
    });
  } else {
    initIndicators(document);
  }

  // Web fonts may swap in after the initial paint and change chip widths
  // (Roboto Condensed vs system fallback). Re-position once fonts settle.
  if (document.fonts && document.fonts.ready && typeof document.fonts.ready.then === 'function') {
    document.fonts.ready.then(function () {
      initIndicators(document);
    });
  }

  // Debounced resize re-position. Chip widths can shift on breakpoint
  // transitions and on address-bar show/hide on mobile.
  let resizeTimer = null;
  window.addEventListener('resize', function () {
    if (resizeTimer) window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(function () {
      initIndicators(document);
    }, 100);
  });

  // product-info.js replaces the entire <variant-selects> subtree on every
  // variant change. Watch for its reinsertion and re-run initIndicators so
  // the sliding indicator is positioned under the correct chip (the active
  // category survives thanks to categoryFilterState living on the module).
  const variantSelectsObserver = new MutationObserver(function (mutations) {
    for (let i = 0; i < mutations.length; i++) {
      const added = mutations[i].addedNodes;
      for (let j = 0; j < added.length; j++) {
        const node = added[j];
        if (!node || node.nodeType !== 1) continue;
        if (node.matches && node.matches('variant-selects')) {
          initIndicators(node);
        } else if (node.querySelectorAll) {
          node.querySelectorAll('variant-selects').forEach(initIndicators);
        }
      }
    }
  });
  variantSelectsObserver.observe(document.body, { childList: true, subtree: true });
})();
